import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useMemo } from "react";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { useUpdateCloseTemplateMutation } from "@api/closeTemplate.api";
import { User } from "@common/types/user";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { HStack } from "@design-system/HStack/HStack";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { CloseTemplateRow } from "../organizationCloseTemplatesViewConfig";

export const CloseTemplateOwnerCell = memo(
    ({ row }: CellContext<ViewTableFeatures, CloseTemplateRow, unknown>) => {
        const templateRow = row.original;
        const { data: members = [] } = useCurrentOrganizationMembers();
        const { error: errorToast } = useToaster();
        const updateTemplate = useUpdateCloseTemplateMutation(templateRow);

        const owner = useMemo(
            () =>
                templateRow.ownerId != null
                    ? (members.find((m) => m.user.id === templateRow.ownerId) ??
                      null)
                    : null,
            [templateRow.ownerId, members],
        );

        const handleUpdate = useCallback(
            async (newOwnerId: User["id"] | null) => {
                if (newOwnerId === templateRow.ownerId) {
                    return;
                }
                try {
                    await updateTemplate.mutateAsync({
                        name: templateRow.name,
                        serviceId: templateRow.serviceId,
                        ownerId: newOwnerId,
                    });
                } catch {
                    errorToast("Failed to update owner");
                }
            },
            [
                updateTemplate,
                templateRow.ownerId,
                templateRow.name,
                templateRow.serviceId,
                errorToast,
            ],
        );

        return (
            <ViewTableCell>
                <Picker.Select<User["id"]>
                    value={owner ? owner.user.id : null}
                    onValueChange={(id) => void handleUpdate(id)}
                >
                    <Picker.CellContent
                        value={
                            owner && (
                                <HStack gap={8} align="center">
                                    <UserAvatar
                                        user={owner.user}
                                        size="sm"
                                        aria-hidden
                                    />
                                    <BreakableText
                                        text={owner.user.name}
                                        showTooltip
                                    />
                                </HStack>
                            )
                        }
                        aria-label={owner?.user.name ?? undefined}
                        addText="Assign owner"
                        hideAddIcon
                    />
                    <Picker.Popup>
                        <Picker.List>
                            <Picker.Item
                                onSelect={() => {
                                    if (templateRow.ownerId != null) {
                                        void handleUpdate(null);
                                    }
                                }}
                            >
                                Unassigned
                            </Picker.Item>
                            {members.map((member) => (
                                <Picker.Item
                                    key={member.user.id}
                                    value={member.user.id}
                                    leading={
                                        <UserAvatar
                                            user={member.user}
                                            size="xs"
                                            aria-hidden
                                        />
                                    }
                                >
                                    {member.user.name}
                                </Picker.Item>
                            ))}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>
            </ViewTableCell>
        );
    },
);
